import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatMoney, formatDate } from "@/lib/utils";
import { getStatusConfig } from "@/config/status-registry";
import { EntityLink } from "@/components/shared/EntityLink";

const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";

function SimplePagination({
  page,
  setPage,
  totalPages,
  isFetching,
}: {
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  isFetching: boolean;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 mt-4 text-xs">
      <span className="text-slate-500 mr-2">
        Trang {page} / {totalPages}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="w-7 h-7"
        disabled={page <= 1 || isFetching}
        onClick={() => setPage(page - 1)}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="w-7 h-7"
        disabled={page >= totalPages || isFetching}
        onClick={() => setPage(page + 1)}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
      {isFetching && (
        <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-2" />
      )}
    </div>
  );
}

// ── ORDERS TAB ──
export function ProductOrdersTab({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["product-orders-paginated", productId, page],
    queryFn: async () =>
      trpcClient.orders.list.query({ productId, page, limit } as any),
    placeholderData: (prev) => prev,
  });

  const ordersList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !ordersList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      {ordersList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có đơn hàng
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Số đơn hàng</TableHead>
                <TableHead className="whitespace-nowrap">Ngày đặt</TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  SL đặt
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  Đơn giá
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  Thành tiền
                </TableHead>
                <TableHead className="text-right whitespace-nowrap text-emerald-700">
                  Đã giao
                </TableHead>
                <TableHead className="whitespace-nowrap">Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordersList.map((o: any) => {
                const oCfg = getStatusConfig("OrderStatus", o.status);
                // Find the detail line(s) matching this product
                const matchingDetails = (o.orderDetails || []).filter(
                  (d: any) => d.productId === productId,
                );
                const totalQty = matchingDetails.reduce(
                  (s: number, d: any) => s + (Number(d.quantity) || 0),
                  0,
                );
                const avgPrice =
                  matchingDetails.length > 0 ? matchingDetails[0].unitPrice : 0;
                const totalAmt = matchingDetails.reduce(
                  (s: number, d: any) => s + (Number(d.amount) || 0),
                  0,
                );
                const totalDelivered = matchingDetails.reduce(
                  (s: number, d: any) => s + (Number(d.qtyDelivered) || 0),
                  0,
                );
                return (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      navigate({
                        to: "/orders/$orderId",
                        params: { orderId: o.id },
                      })
                    }
                  >
                    <TableCell className="font-mono text-xs font-medium">
                      <EntityLink
                        entityType="order"
                        id={o.id}
                        code={o.orderId || o.id}
                        search={{ returnUrl: `/products/${productId}?tab=orders` }}
                      />
                    </TableCell>
                    <TableCell>{fmtD(o.orderDate)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatNumber(totalQty)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(avgPrice)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {formatMoney(totalAmt)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700">
                      {formatNumber(totalDelivered)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={oCfg.color}>
                        {oCfg.label || o.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}

// ── DELIVERIES TAB ──
export function ProductDeliveriesTab({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["product-deliveries-paginated", productId, page],
    queryFn: async () =>
      trpcClient.deliveries.list.query({ productId, page, limit } as any),
    placeholderData: (prev) => prev,
  });

  const deliveriesList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !deliveriesList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      {deliveriesList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có phiếu giao hàng
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Mã PGH</TableHead>
                <TableHead className="whitespace-nowrap">Ngày giao</TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  Số lượng
                </TableHead>
                <TableHead className="whitespace-nowrap">Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveriesList.map((d: any) => {
                const dCfg = getStatusConfig("DeliveryStatus", d.status);
                return (
                  <TableRow
                    key={d.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      navigate({
                        to: "/delivery/$deliveryId",
                        params: { deliveryId: d.id },
                      } as any)
                    }
                  >
                    <TableCell className="font-mono text-xs font-medium">
                      <EntityLink
                        entityType="delivery"
                        id={d.id}
                        code={d.deliveryId || d.id}
                        search={{ returnUrl: `/products/${productId}?tab=deliveries` }}
                      />
                    </TableCell>
                    <TableCell>{fmtD(d.deliveryDate)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(
                        d.deliveryItems
                          ?.filter((di: any) => di.productId === productId)
                          .reduce(
                            (s: number, di: any) =>
                              s + (Number(di.deliveredQty) || 0),
                            0,
                          ) || 0,
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={dCfg.color}>
                        {dCfg.label || d.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}
